import { throttle } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
/**
 * Creates a throttled version of a function that only executes at most once per specified time interval.
 *
 * @template Args - The argument types of the function
 * @template Return - The return type of the function
 * @param {(...args: Args) => Return} fn - The function to throttle
 * @param {number} throttleMs - The minimum time in milliseconds between function executions
 * @param {UseThrottleOptions} options - Configuration options for throttle behavior
 * @param {boolean} [options.leading=true] - Execute on the leading edge of the timeout
 * @param {boolean} [options.trailing=true] - Execute on the trailing edge of the timeout
 * @returns {(...args: Args) => Return | undefined} A throttled version of the function
 */
export const useThrottle = (fn, throttleMs, { leading = true, trailing = true } = {}) => {
    const fnRef = useRef(fn);
    useEffect(() => {
        fnRef.current = fn;
    }, [fn]);
    const throttledFn = useMemo(() => throttle((...args) => fnRef.current(...args), throttleMs, {
        leading,
        trailing,
    }), [throttleMs, leading, trailing]);
    useEffect(() => {
        return () => {
            throttledFn.cancel();
        };
    }, [throttledFn]);
    return throttledFn;
};
//# sourceMappingURL=useThrottle.js.map